import * as THREE from 'three';
import { createSeededRandom } from '../utils/random';

const WING_COLOR = 0xf6f7f4;
const BODY_COLOR = 0xfbfbf8;

const MIN_COUNT = 8;
const MAX_COUNT = 12;

const MIN_RADIUS = 30;
const MAX_RADIUS = 80;

const MIN_HEIGHT = 15;
const MAX_HEIGHT = 40;

const MIN_SPEED = 6;
const MAX_SPEED = 13;

const FLAP_AMPLITUDE = THREE.MathUtils.degToRad(15);

interface Gull {
  readonly group: THREE.Group;
  readonly tilt: THREE.Group;
  readonly leftWing: THREE.Mesh;
  readonly rightWing: THREE.Mesh;
  readonly radius: number;
  readonly angularSpeed: number;
  readonly height: number;
  readonly bobAmplitude: number;
  readonly bobSpeed: number;
  readonly bobPhase: number;
  readonly flapSpeed: number;
  readonly flapPhase: number;
  readonly diveDepth: number;
  readonly diveDuration: number;
  angle: number;
  diveTimer: number;
  diving: boolean;
  diveProgress: number;
}

/**
 * Ambient flock of low-poly seagulls that loosely orbits the shared ship.
 *
 * Each bird is a tiny V-shaped glider (two flat triangle wings plus a small
 * body) that flaps with a subtle sine wave. The flock follows the ship by
 * recentring every orbit on the ship's world position, so the birds stay
 * around the action while the ship sails. A seeded RNG keeps radii, heights,
 * speeds and dive timings deterministic across runs.
 */
export class Seagulls {
  private readonly group = new THREE.Group();
  private readonly gulls: Gull[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly rng: () => number;

  constructor(scene: THREE.Scene) {
    this.group.name = 'seagulls';
    scene.add(this.group);

    const rng = createSeededRandom(4242);
    this.rng = rng;

    // One wing geometry/triangle, shared by both wings; the left wing is
    // mirrored with scale.x = -1. Flat in local XZ so a rotation about Z raises
    // and lowers the wing tip.
    const wingGeometry = this.geometry(
      new THREE.BufferGeometry().setAttribute(
        'position',
        new THREE.Float32BufferAttribute(
          // root leading edge, root trailing edge, swept-back tip
          [0, 0, 0.1, 0, 0, -0.1, 1.15, 0, -0.28],
          3,
        ),
      ),
    );
    wingGeometry.computeVertexNormals();

    const wingMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: WING_COLOR,
        roughness: 0.85,
        metalness: 0,
        side: THREE.DoubleSide,
        flatShading: true,
      }),
    );

    const bodyGeometry = this.geometry(new THREE.SphereGeometry(0.12, 6, 5));
    const bodyMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: BODY_COLOR,
        roughness: 0.8,
        metalness: 0,
        flatShading: true,
      }),
    );

    const count = MIN_COUNT + Math.floor(rng() * (MAX_COUNT - MIN_COUNT + 1));
    for (let i = 0; i < count; i += 1) {
      const group = new THREE.Group();

      // Inner node carries dive pitch/bank so yaw on the parent stays clean.
      const tilt = new THREE.Group();
      group.add(tilt);

      const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
      body.scale.set(1, 0.8, 2.2);
      tilt.add(body);

      const rightWing = new THREE.Mesh(wingGeometry, wingMaterial);
      const leftWing = new THREE.Mesh(wingGeometry, wingMaterial);
      leftWing.scale.x = -1;
      tilt.add(rightWing, leftWing);

      const radius = THREE.MathUtils.lerp(MIN_RADIUS, MAX_RADIUS, rng());
      const speed = THREE.MathUtils.lerp(MIN_SPEED, MAX_SPEED, rng());
      const direction = rng() < 0.5 ? -1 : 1;

      this.gulls.push({
        group,
        tilt,
        leftWing,
        rightWing,
        radius,
        // Constant linear speed regardless of radius keeps the flock coherent.
        angularSpeed: (speed / radius) * direction,
        height: THREE.MathUtils.lerp(MIN_HEIGHT, MAX_HEIGHT, rng()),
        bobAmplitude: THREE.MathUtils.lerp(1, 3, rng()),
        bobSpeed: THREE.MathUtils.lerp(0.3, 0.8, rng()),
        bobPhase: rng() * Math.PI * 2,
        flapSpeed: THREE.MathUtils.lerp(3.5, 6, rng()),
        flapPhase: rng() * Math.PI * 2,
        diveDepth: THREE.MathUtils.lerp(8, 14, rng()),
        diveDuration: THREE.MathUtils.lerp(2.5, 4, rng()),
        angle: rng() * Math.PI * 2,
        diveTimer: THREE.MathUtils.lerp(6, 24, rng()),
        diving: false,
        diveProgress: 0,
      });

      this.group.add(group);
    }
  }

  /**
   * Animates the flock around the ship. `elapsed` drives flap/bob phases and
   * `shipX/Y/Z` is the orbit centre (the ship's world position).
   */
  update(delta: number, elapsed: number, shipX: number, shipY: number, shipZ: number): void {
    for (const gull of this.gulls) {
      gull.angle += gull.angularSpeed * delta;

      // Dive cycle: idle for a while, then swoop toward the water and climb.
      if (!gull.diving) {
        gull.diveTimer -= delta;
        if (gull.diveTimer <= 0) {
          gull.diving = true;
          gull.diveProgress = 0;
        }
      } else {
        gull.diveProgress += delta / gull.diveDuration;
        if (gull.diveProgress >= 1) {
          gull.diving = false;
          gull.diveProgress = 0;
          gull.diveTimer = THREE.MathUtils.lerp(8, 26, this.rng());
        }
      }

      const diveArc = gull.diving ? Math.sin(Math.PI * Math.min(gull.diveProgress, 1)) : 0;
      const diveDrop = diveArc * gull.diveDepth;

      const cos = Math.cos(gull.angle);
      const sin = Math.sin(gull.angle);
      const x = shipX + gull.radius * cos;
      const z = shipZ + gull.radius * sin;
      // Heights are measured above the water plane; a fraction of the ship's
      // vertical motion is added so the flock drifts with the swell.
      const y =
        gull.height +
        Math.sin(elapsed * gull.bobSpeed + gull.bobPhase) * gull.bobAmplitude -
        diveDrop +
        shipY * 0.3;

      gull.group.position.set(x, y, z);

      // Face the direction of travel (tangent to the orbit). Forward is local +Z.
      const dirX = -sin * Math.sign(gull.angularSpeed);
      const dirZ = cos * Math.sign(gull.angularSpeed);
      gull.group.rotation.y = Math.atan2(dirX, dirZ);

      // Nose down during the dive, gentle bank into the turn otherwise.
      gull.tilt.rotation.x = diveArc * 0.4;
      gull.tilt.rotation.z = -Math.sign(gull.angularSpeed) * 0.12 * (1 - diveArc);

      const flap = Math.sin(elapsed * gull.flapSpeed + gull.flapPhase) * FLAP_AMPLITUDE;
      const diveBoost = 1 + diveArc * 0.8;
      gull.rightWing.rotation.z = flap * diveBoost;
      gull.leftWing.rotation.z = -flap * diveBoost;
    }
  }

  dispose(): void {
    this.group.removeFromParent();
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
    this.gulls.length = 0;
  }

  private geometry<T extends THREE.BufferGeometry>(geometry: T): T {
    this.geometries.push(geometry);
    return geometry;
  }

  private material<T extends THREE.Material>(material: T): T {
    this.materials.push(material);
    return material;
  }
}
